import type { ICalculateAdjustmentsResponse, IQualityAdjustmentResult, SpecType } from '@local/lib';

import { QualityAdjustmentType as ApiQualityAdjustmentType, QualityFormulaType } from '@local/lib';

import type {
  QualityFormulaPreviewResult,
  QualityFormulaPreviewSummary,
  SingleValueFormulaRow,
  SpecDefinition,
  SpecState,
  ThresholdFormulaRow,
} from './qualityFormulaTypes';

import { FORMULA_TYPES, getSpecUnit, SPEC_TYPES } from './qualityFormulaMetadata';

export interface MapCalculateAdjustmentsResponseInput {
  response: ICalculateAdjustmentsResponse;
  specStates: Record<SpecType, SpecState>;
}

export interface MapCalculateAdjustmentsResponseOutput {
  results: QualityFormulaPreviewResult[];
  summary: QualityFormulaPreviewSummary;
}

const buildQuantityResult = (
  spec: SpecDefinition,
  adjustment: IQualityAdjustmentResult,
  formulaLabel: string,
  shippedQuantity: number,
  adjustedInvoiceQuantity: number,
  specState: SpecState,
): QualityFormulaPreviewResult => ({
  adjustedInvoiceQuantity,
  // 0 for QuantityAdjustedByPercent (e.g. Nitrogen) — it has no contracted baseline, so row 0's
  // lowerBound is an unused placeholder rather than a real value (see createSingleValueRowShape).
  contractedValue: (specState.rows[0] as SingleValueFormulaRow).lowerBound,
  description: adjustment.description,
  direction: 'discount',
  formulaLabel,
  id: `${spec.key}-quantity`,
  incrementLabel: null,
  kind: 'quantity',
  matchedTier: null,
  shippedQuantity,
  specLabel: spec.label,
  testValue: adjustment.actualSpec,
  totalImpact: shippedQuantity - adjustedInvoiceQuantity,
  unitImpact: null,
});

const getPriceDirection = (
  formulaType: SpecState['formulaType'],
  unitImpact: number,
): QualityFormulaPreviewResult['direction'] => {
  if (formulaType === QualityFormulaType.AdjustedSalesPrice) return 'adjusted';
  return unitImpact > 0 ? 'premium' : 'discount';
};

interface BuildPriceResultOutput {
  result: QualityFormulaPreviewResult;
  unitImpact: number;
}

const buildPriceResult = (
  spec: SpecDefinition,
  adjustment: IQualityAdjustmentResult,
  formulaLabel: string,
  specState: SpecState,
): BuildPriceResultOutput => {
  const { contractedSpec, formulaType, rows } = specState;
  const unitImpact = adjustment.priceAdjustmentPerUnit ?? 0;
  const isTiered = formulaType === QualityFormulaType.Percentage || formulaType === QualityFormulaType.PerUnit;
  const { matchedTierIndex } = adjustment;
  const contractedValue = isTiered ? contractedSpec : (rows[0] as SingleValueFormulaRow).contractedSpec;
  const matchedRow = (matchedTierIndex ? rows[matchedTierIndex - 1] : rows[0]) as ThresholdFormulaRow;
  const incrementLabel = isTiered ? `Every ${matchedRow.perIncrement} ${getSpecUnit(spec.key)}` : null;

  return {
    result: {
      contractedValue,
      description: adjustment.description,
      direction: getPriceDirection(formulaType, unitImpact),
      formulaLabel,
      id: `${spec.key}-preview`,
      incrementLabel,
      kind: 'price',
      matchedTier: matchedTierIndex,
      specLabel: spec.label,
      testValue: adjustment.actualSpec,
      totalImpact: adjustment.priceAdjustmentTotal,
      unitImpact,
    },
    unitImpact,
  };
};

// Maps the API's calculation response back onto the playground's display model. All pricing math
// and tier matching has already been performed server-side (QualityAdjustmentManager) — this only
// formats values (and reads back `matchedTierIndex`) for presentation, with nothing recomputed.
export const mapCalculateAdjustmentsResponseToPreviewResults = ({
  response,
  specStates,
}: MapCalculateAdjustmentsResponseInput): MapCalculateAdjustmentsResponseOutput => {
  const resultEntries = SPEC_TYPES.flatMap(spec => {
    const specState = specStates[spec.key];
    if (!specState.enabled) return [];

    const adjustment = response.adjustments[spec.key];
    if (!adjustment) return [];

    const formulaLabel =
      FORMULA_TYPES.find(option => option.value === specState.formulaType)?.label ?? specState.formulaType;

    if (adjustment.adjustmentType === ApiQualityAdjustmentType.Quantity) {
      // At most one quantity-type spec is ever active (CalculateAdjustmentsRequestSchema
      // enforces this), so its card reads the response's own combined adjustedInvoiceQuantity.
      const result = buildQuantityResult(
        spec,
        adjustment,
        formulaLabel,
        response.shippedQuantity,
        response.adjustedInvoiceQuantity,
        specState,
      );
      return [{ result, unitImpact: 0 }];
    }

    return [buildPriceResult(spec, adjustment, formulaLabel, specState)];
  });

  return {
    results: resultEntries.map(entry => entry.result),
    summary: {
      finalPrice: resultEntries.reduce((total, entry) => total + entry.unitImpact, response.tradePrice),
      finalQuantity: response.adjustedInvoiceQuantity,
      startingPrice: response.tradePrice,
      startingQuantity: response.shippedQuantity,
    },
  };
};
